import { formatNumber, NgTemplateOutlet } from '@angular/common';
import { booleanAttribute, Component, computed, inject, input, LOCALE_ID, output, TemplateRef } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyTableSelectionService } from '../../services/table-selection.service';
import { resolveSelectionBarState, SelectionBarState } from './selection-bar-state';

/**
 * Context handed to the copy override templates. `$implicit` is the number the current state is
 * about: the matching-record total in select-all mode, the selected count everywhere else.
 */
export interface SelectionBarCopyContext {
  $implicit: number;
  count: number;
  total: number;
  totalKnown: boolean;
  countLabel: string;
  totalLabel: string;
}

@Component({
  selector: 'glxy-table-selection-bar',
  templateUrl: './table-selection-bar.component.html',
  styleUrls: ['./table-selection-bar.component.scss'],
  imports: [NgTemplateOutlet, MatButtonModule, MatIconModule, MatMenuModule, TranslateModule],
})
export class TableSelectionBarComponent {
  public readonly selectAllAvailable = input(false, { transform: booleanAttribute });
  public readonly showActions = input(true, { transform: booleanAttribute });
  public readonly hideActionButton = input(false, { transform: booleanAttribute });
  public readonly pageScopeTemplate = input<TemplateRef<SelectionBarCopyContext> | undefined>(undefined);
  public readonly selectAllPromptTemplate = input<TemplateRef<SelectionBarCopyContext> | undefined>(undefined);
  public readonly allMatchingTemplate = input<TemplateRef<SelectionBarCopyContext> | undefined>(undefined);

  public readonly selectAllToggled = output<void>();
  public readonly selectionCleared = output<void>();

  protected readonly state = computed<SelectionBarState>(() =>
    resolveSelectionBarState({
      selectedCount: this.selectionService.selectedCount(),
      totalRecords: this.selectionService.totalRecords(),
      selectAllMode: this.selectionService.selectAllMode(),
      selectAllAvailable: this.selectAllAvailable(),
      rowsSelectionLimit: this.selectionService.rowsSelectionLimit,
      showActions: this.showActions(),
    }),
  );

  protected readonly copyContext = computed<SelectionBarCopyContext>(() => {
    const state = this.state();
    const count = this.selectionService.selectedCount();
    const total = 'total' in state ? state.total : 0;
    const totalKnown = 'totalKnown' in state ? state.totalKnown : false;
    return {
      $implicit: state.kind === 'allMatching' ? total : count,
      count,
      total,
      totalKnown,
      countLabel: formatNumber(count, this.locale),
      totalLabel: totalKnown ? formatNumber(total, this.locale) : '',
    };
  });

  private readonly selectionService = inject(GalaxyTableSelectionService);
  private readonly locale = inject(LOCALE_ID);

  protected onSelectAll(): void {
    this.selectAllToggled.emit();
  }

  protected onClear(): void {
    this.selectionService.clear();
    this.selectionCleared.emit();
  }
}
